import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutGrid, ListTodo, Layers, ShieldCheck, FileText, FolderCheck,
  AlertTriangle, Building2, Activity, Plug, ClipboardCheck, FileBarChart,
  Users, FileCheck2, Siren, Boxes, GraduationCap,
} from "lucide-react";
import ComplianceDashboard from "../components/compliance/ComplianceDashboard";
import ComplianceTasks from "../components/compliance/ComplianceTasks";
import ComplianceFrameworks from "../components/compliance/ComplianceFrameworks";
import ComplianceFrameworksGrid from "../components/compliance/ComplianceFrameworksGrid";
import ComplianceControls from "../components/compliance/ComplianceControls";
import CompliancePolicies from "../components/compliance/CompliancePolicies";
import ComplianceEvidence from "../components/compliance/ComplianceEvidence";
import ComplianceRisks from "../components/compliance/ComplianceRisks";
import ComplianceVendors from "../components/compliance/ComplianceVendors";
import ComplianceAssets from "../components/compliance/ComplianceAssets";
import ComplianceTraining from "../components/compliance/ComplianceTraining";
import ComplianceMonitoring from "../components/compliance/ComplianceMonitoring";
import ComplianceIntegrations from "../components/compliance/ComplianceIntegrations";
import ComplianceAudits from "../components/compliance/ComplianceAudits";
import ComplianceIncidents from "../components/compliance/ComplianceIncidents";
import ComplianceReports from "../components/compliance/ComplianceReports";
import CompliancePeople from "../components/compliance/CompliancePeople";
import ComplianceTrustCenter from "../components/compliance/ComplianceTrustCenter";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutGrid },
  { key: "tasks", label: "Tasks", icon: ListTodo },
  { key: "frameworks", label: "Frameworks", icon: Layers },
  { key: "controls", label: "Controls", icon: ShieldCheck },
  { key: "policies", label: "Policies", icon: FileText },
  { key: "training", label: "Training", icon: GraduationCap },
  { key: "evidence", label: "Evidence", icon: FolderCheck },
  { key: "risks", label: "Risk Register", icon: AlertTriangle },
  { key: "vendors", label: "Vendors", icon: Building2 },
  { key: "assets", label: "Asset Inventory", icon: Boxes },
  { key: "monitoring", label: "Monitoring", icon: Activity },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "audits", label: "Audits", icon: ClipboardCheck },
  { key: "incidents", label: "Incidents", icon: Siren },
  { key: "trust", label: "Trust Center", icon: ShieldCheck },
  { key: "reports", label: "Reports", icon: FileBarChart },
  { key: "people", label: "People", icon: Users },
];

export default function Compliance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));
  const [tab, setTab] = useState("dashboard");
  const [controlsFilter, setControlsFilter] = useState("all");

  const goToControls = (frameworkKey) => {
    setControlsFilter(frameworkKey || "all");
    setTab("controls");
  };

  const renderTab = () => {
    switch (tab) {
      case "dashboard": return <ComplianceDashboard onNavigate={setTab} onViewFrameworksGrid={() => setTab("frameworks-grid")} />;
      case "frameworks-grid": return <ComplianceFrameworksGrid onBack={() => setTab("dashboard")} onViewControls={goToControls} />;
      case "tasks": return <ComplianceTasks isAdmin={isAdmin} />;
      case "frameworks": return <ComplianceFrameworks isAdmin={isAdmin} onViewControls={goToControls} />;
      case "controls": return <ComplianceControls isAdmin={isAdmin} initialFrameworkFilter={controlsFilter} />;
      case "policies": return <CompliancePolicies isAdmin={isAdmin} />;
      case "training": return <ComplianceTraining />;
      case "evidence": return <ComplianceEvidence isAdmin={isAdmin} />;
      case "risks": return <ComplianceRisks isAdmin={isAdmin} />;
      case "vendors": return <ComplianceVendors isAdmin={isAdmin} />;
      case "assets": return <ComplianceAssets isAdmin={isAdmin} />;
      case "monitoring": return <ComplianceMonitoring isAdmin={isAdmin} />;
      case "integrations": return <ComplianceIntegrations isAdmin={isAdmin} />;
      case "audits": return <ComplianceAudits isAdmin={isAdmin} />;
      case "incidents": return <ComplianceIncidents isAdmin={isAdmin} />;
      case "trust": return <ComplianceTrustCenter />;
      case "reports": return <ComplianceReports />;
      case "people": return <CompliancePeople isAdmin={isAdmin} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-4" data-testid="compliance-page">
      <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1 flex-wrap">
        <Link to="/" className="hover:text-foreground">Dashboard</Link>
        <span aria-hidden>›</span>
        <Link to="/apps" className="hover:text-foreground">Apps</Link>
        <span aria-hidden>›</span>
        <span className="text-foreground font-medium">UC Compliance</span>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
          <FileCheck2 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight leading-tight">UC Compliance</h1>
          <p className="text-[13px] text-muted-foreground mt-0.5">
            Frameworks, controls, policies, risk & audit readiness.
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-5 lg:gap-6">
        {/* Vertical nav — desktop/tablet (lg and up) */}
        <nav
          className="hidden lg:flex lg:flex-col lg:w-56 lg:shrink-0 gap-1 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto tabler-scroll"
          data-testid="compliance-tabs-vertical"
        >
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`compliance-tab-${t.key}`}
                className={
                  "flex items-center gap-2.5 h-10 px-3 rounded-lg text-[13px] font-medium whitespace-nowrap transition-colors text-left " +
                  (active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50")
                }
              >
                <Icon className="w-4 h-4 shrink-0" /> {t.label}
                {t.comingSoon && <span className="text-[9px] opacity-70">(soon)</span>}
              </button>
            );
          })}
        </nav>

        {/* Horizontal pill nav — mobile/small-tablet fallback (below lg) */}
        <div className="flex lg:hidden items-center gap-2 border-b border-border pb-3 overflow-x-auto tabler-scroll" data-testid="compliance-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`compliance-tab-mobile-${t.key}`}
                className={
                  "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors border " +
                  (active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:text-foreground hover:border-primary/40")
                }
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
                {t.comingSoon && <span className="text-[9px] opacity-70">(soon)</span>}
              </button>
            );
          })}
        </div>

        <div className="flex-1 min-w-0" data-testid="compliance-tab-content">{renderTab()}</div>
      </div>
    </div>
  );
}
